import { useEffect, useState } from "react";
import { inquiryTypes } from "../data/content";
import { siteConfig } from "../config/site";

function FlexibleField({ name, label, placeholder, inputMode }) {
  const [unknown, setUnknown] = useState(false);
  const id = `inquiry-${name}`;

  return <div className="form-field">
    <label htmlFor={id}>{label}</label>
    <div className={`form-flexible-row${unknown ? " is-unknown" : ""}`}>
      <input id={id} name={unknown ? undefined : name} required={!unknown} disabled={unknown} placeholder={unknown ? "아직 정해지지 않았습니다" : placeholder} inputMode={inputMode} />
      {unknown && <input type="hidden" name={name} value="미정" />}
      <label className="unknown-toggle">
        <input type="checkbox" checked={unknown} onChange={(event) => setUnknown(event.target.checked)} />
        <span>미정</span>
      </label>
    </div>
  </div>;
}

function OptionalLabel({ children }) {
  return <>{children}<span className="form-optional">선택</span></>;
}

export default function InquiryForm() {
  const [kind, setKind] = useState("unsure");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const getEstimatedReplyTime = () => new Date(Date.now() + 9 * 60 * 1000).toLocaleTimeString("ko-KR", { hour: "numeric", minute: "2-digit" });
  const [estimatedReplyTime, setEstimatedReplyTime] = useState("");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (["visit", "institution"].includes(q.get("type"))) setKind(q.get("type"));
    const updateReplyTime = () => setEstimatedReplyTime(getEstimatedReplyTime());
    updateReplyTime();
    const timer = window.setInterval(updateReplyTime, 30000);
    return () => window.clearInterval(timer);
  }, []);

  async function submit(event) {
    event.preventDefault();
    if (!siteConfig.formEndpoint || siteConfig.formEndpoint === "FORM_ENDPOINT") {
      setStatus("not-configured");
      setMessage("현재 문의 전송 주소가 연결되지 않았습니다. 운영자가 VITE_FORM_ENDPOINT를 설정하면 이 양식에서 바로 전송할 수 있습니다.");
      return;
    }
    setStatus("sending");
    setMessage("");
    try {
      const response = await fetch(siteConfig.formEndpoint, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(Object.fromEntries(new FormData(event.currentTarget))),
      });
      if (!response.ok) throw new Error();
      setStatus("done");
    } catch {
      setStatus("error");
      setMessage("전송에 실패했습니다. 잠시 후 다시 시도하거나 전화·이메일로 문의해 주세요.");
    }
  }

  if (status === "done") return <div className="form-complete" role="status"><span>✓</span><h2>문의가 접수되었습니다</h2><p>입력하신 연락처로 10분 이내에 답변드리겠습니다.</p><button type="button" onClick={() => setStatus("idle")}>새 문의 작성</button></div>;

  return <form className="inquiry-form" onSubmit={submit}>
    <input className="form-honeypot" type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true" />
    <fieldset>
      <legend>어떤 공연을 문의하시나요?</legend>
      <div className="type-options">{Object.entries(inquiryTypes).map(([key, label]) => <label key={key} className={kind === key ? "selected" : ""}><input type="radio" name="type" value={key} checked={kind === key} onChange={() => setKind(key)} /><span>{label}</span></label>)}</div>
    </fieldset>

    <div className="form-grid">
      <label>기관명<input name="organization" required autoComplete="organization" placeholder="예: ○○어린이집 또는 ○○문화재단" /></label>
      <label>담당자명<input name="name" required autoComplete="name" placeholder="예: 홍길동" /></label>
      <label>연락처<input name="phone" inputMode="tel" required autoComplete="tel" placeholder="예: 010-1234-5678" /><small className="contact-use-note">문의 답변에만 사용하며, 광고·홍보 목적으로 사용하지 않습니다.</small></label>

      {kind === "institution" && <>
        <label>견적서 받을 이메일<input name="email" type="email" required autoComplete="email" /></label>
        <FlexibleField key="institution-date" name="date" label="희망 날짜 또는 시기" placeholder="예: 10월 중, 평일 오전" />
        <FlexibleField key="institution-region" name="region" label="공연 지역" placeholder="예: 경기도 화성시" />
        <FlexibleField key="institution-count" name="count" label="예상 인원" placeholder="대략적인 인원도 괜찮습니다" inputMode="numeric" />
        <label><OptionalLabel>희망 공연 횟수</OptionalLabel><input name="sessions" placeholder="예: 1회, 같은 날 2회" /></label>
        <label className="full"><OptionalLabel>행사 예산</OptionalLabel><input name="budget" placeholder="총예산과 부가세 포함 여부를 적어주세요" /></label>
      </>}

      {kind === "visit" && <>
        <FlexibleField key="visit-date" name="date" label="희망 방문일 또는 시기" placeholder="예: 10월 중, 평일 오전" />
        <FlexibleField key="visit-count" name="count" label="아이 인원" placeholder="대략적인 인원도 괜찮습니다" inputMode="numeric" />
      </>}

      {kind === "unsure"
        ? <label className="full">문의 사항<textarea name="message" rows="5" required placeholder="궁금한 점이나 필요한 안내를 자유롭게 적어주세요." /></label>
        : <label className="full"><OptionalLabel>기타 요청사항</OptionalLabel><textarea name="message" rows="5" placeholder="공연 목적이나 궁금한 점을 자유롭게 적어주세요." /></label>}
    </div>
    <div className="reply-eta" aria-label={estimatedReplyTime ? `지금 문의하면 ${estimatedReplyTime}까지 답변 예상` : "10분 이내 답변 예상"}>
      <div><span><i aria-hidden="true" />지금 문의하면</span><small>현재 예상 답변 시간 · 최대 10분</small></div>
      <strong>{estimatedReplyTime || "10분 이내"}{estimatedReplyTime && <small>까지</small>}</strong>
    </div>
    <button className="button wine submit" type="submit" disabled={status === "sending"}>{status === "sending" ? "문의 전송 중…" : "문의 내용 제출하기"}<span aria-hidden="true">↗</span></button>
    {message && <p className={`form-feedback ${status}`} role="alert">{message}</p>}
  </form>;
}
